import React, { useContext, createContext, useRef, useState } from "react";
import RewriteSuggestionCarousel from "../RewriteSuggestionCarousel";
import { LiteralReplacementResult } from "./LiteralReplacementResult";
import type { IndependentPreviewGroup as PreviewGroup } from "./independentPreviewGroups";
import { manuscriptChangeAppliedSuggestionIndex, manuscriptChangeStatusShowsDurableUndo, manuscriptChangeSuggestionEntries } from "./manuscriptChangeMessageRef";
import { applyManuscriptChangeSet, approveManuscriptChange, undoManuscriptChangeSet, safeManuscriptChangeApplyFailureMessage } from "src/lib/manuscriptChangeActions";
import { dispatchAppliedManuscriptChangeHighlight } from "src/lib/dispatchAppliedManuscriptChangeHighlight";

type GroupState = { localStatus: string | null; appliedIndex: number | null; pending: boolean; error: string | null };
const initialState: GroupState = { localStatus: null, appliedIndex: null, pending: false, error: null };
const GroupStateContext = createContext<{
  states: Record<string, GroupState>;
  setStates: React.Dispatch<React.SetStateAction<Record<string, GroupState>>>;
  busy: React.MutableRefObject<Set<string>>;
} | null>(null);

/** Ordered cards for one proposal still share selection and pending state. */
export function PreviewGroupStateProvider({ children }: { children: React.ReactNode }) {
  const [states, setStates] = useState<Record<string, GroupState>>({});
  const busy = useRef(new Set<string>());
  return <GroupStateContext.Provider value={{ states, setStates, busy }}>{children}</GroupStateContext.Provider>;
}

type Props = {
  group: PreviewGroup;
  visibleOptionIndex?: number;
  hideTitle?: boolean;
  chatId: string;
  projectId: string;
  streaming?: boolean;
  refetch?: () => unknown;
  onSendSuggestionToNewDocument: React.ComponentProps<typeof RewriteSuggestionCarousel>["onSendSuggestionToNewDocument"];
};

/** Each chapter owns its persisted action IDs and its local apply/undo state. */
export function IndependentPreviewGroup({ group, visibleOptionIndex, hideTitle, chatId, projectId, streaming, refetch, onSendSuggestionToNewDocument }: Props) {
  const change = group.change;
  const shared = useContext(GroupStateContext);
  const [ownState, setOwnState] = useState(initialState);
  const ownBusy = useRef(new Set<string>());
  const busy = shared?.busy ?? ownBusy;
  const { localStatus, appliedIndex, pending, error } = shared?.states[group.key] ?? ownState;
  const patch = (next: Partial<GroupState>) => shared
    ? shared.setStates(previous => ({ ...previous, [group.key]: { ...(previous[group.key] ?? initialState), ...next } }))
    : setOwnState(previous => ({ ...previous, ...next }));
  const setLocalStatus = (localStatus: string | null) => patch({ localStatus });
  const setAppliedIndex = (appliedIndex: number | null) => patch({ appliedIndex });
  const setPending = (pending: boolean) => patch({ pending });
  const setError = (error: string | null) => patch({ error });
  const members = group.changes ?? [change];
  const undoMember = members.find(member => manuscriptChangeStatusShowsDurableUndo(member.status));
  const status = localStatus ?? undoMember?.status ?? change.status;
  const entries = manuscriptChangeSuggestionEntries({ manuscriptChange: change });
  const canApply = !streaming && !pending && Boolean(chatId && projectId && change.documentId)
    && (!change.changeSetId || Boolean(change.proposalGroupId))
    && members.every(member => ["awaiting_consent", "ready_to_apply", "needs_review_again"].includes(localStatus ?? member.status));
  const selectedIndex = localStatus
    ? manuscriptChangeStatusShowsDurableUndo(localStatus) ? appliedIndex : null
    : manuscriptChangeAppliedSuggestionIndex(undoMember ?? change, group.suggestions);

  const apply = async (_text: string, index = 0) => {
    if (!canApply || busy.current.has(group.key)) return;
    busy.current.add(group.key);
    setPending(true);
    setError(null);
    try {
      let nextStatus: string;
      let appliedChanges: Set<string> | undefined;
      const selectedEntry = entries[index];
      const optionId = selectedEntry?.optionId ?? (index === 0 && group.suggestions.length <= 1 ? change.changeId : undefined);
      if (!optionId) throw new Error("The selected option is unavailable. Refresh and try again.");
      if (change.changeSetId && change.proposalGroupId) {
        const result = await applyManuscriptChangeSet({ changeSetId: change.changeSetId, proposalGroupId: change.proposalGroupId,
          optionId, chatId, projectId });
        nextStatus = result.status;
        appliedChanges = new Set(result.documents
          .filter(document => document.status === "applied" || document.status === "visible")
          .map(document => JSON.stringify([document.documentId, document.changeId])));
        if (nextStatus !== "applied") setError(result.message ?? "I couldn’t fully apply that change.");
      } else {
        const result = await approveManuscriptChange({ changeId: optionId, documentId: selectedEntry?.documentId ?? change.documentId!, chatId, projectId });
        nextStatus = result.outcome === "applied" ? "applied" : result.status ?? status;
        if (result.outcome !== "applied") setError("I couldn’t apply that change. Refresh and try again.");
      }
      if (manuscriptChangeStatusShowsDurableUndo(nextStatus)) {
        setLocalStatus(nextStatus);
        setAppliedIndex(index);
      }
      if (nextStatus === "applied" && !group.compact) {
        for (const member of group.changes ?? [change]) {
          const entry = manuscriptChangeSuggestionEntries({ manuscriptChange: member })[index];
          const documentId = entry?.documentId ?? member.documentId;
          const changeId = entry?.optionId ?? (index === 0 ? member.changeId : undefined);
          if (appliedChanges && !appliedChanges.has(JSON.stringify([documentId, changeId]))) continue;
          dispatchAppliedManuscriptChangeHighlight(documentId!, entry?.text ?? member.preview?.replacementText ?? member.preview?.after);
        }
      }
      refetch?.();
    } catch (cause) {
      setError(safeManuscriptChangeApplyFailureMessage(cause));
    } finally {
      busy.current.delete(group.key);
      setPending(false);
    }
  };
  const undo = async () => {
    if (!change.changeSetId || busy.current.has(group.key) || streaming) return false;
    busy.current.add(group.key);
    setPending(true);
    setError(null);
    try {
      const result = await undoManuscriptChangeSet({ changeSetId: change.changeSetId, chatId, projectId });
      if (result.status === "undone" || manuscriptChangeStatusShowsDurableUndo(result.status)) setLocalStatus(result.status);
      if (result.status === "undone") setAppliedIndex(null);
      else setError(result.message ?? "I couldn’t safely undo that change.");
      refetch?.();
      return result.status === "undone";
    } catch (cause) {
      setError("I couldn’t safely undo that change. Please try again.");
      return false;
    } finally {
      busy.current.delete(group.key);
      setPending(false);
    }
  };
  return <section aria-label={group.title} className="flex flex-col gap-3">
    {!hideTitle && <h3 className="m-0 text-base font-semibold text-app-grey-0">{group.title}</h3>}
    {group.compact ? (group.changes ?? [change]).map(member => <LiteralReplacementResult key={member.changeId}
      change={{ ...member, status: localStatus ?? member.status }} wholeScope={!member.preview?.literalReplacements?.length}
      allowApply={canApply} allowUndo={!streaming && !pending && Boolean(chatId && projectId && change.changeSetId) && manuscriptChangeStatusShowsDurableUndo(status)}
      onApply={() => { void apply(""); }} onUndo={undo} />) : <RewriteSuggestionCarousel layout="stack" writeSuggestionCardHeader={{ mode: "auto" }} suggestions={visibleOptionIndex === undefined ? group.suggestions : [group.suggestions[visibleOptionIndex]]}
      labels={visibleOptionIndex === undefined ? entries.map(entry => entry.label) : [entries[visibleOptionIndex]?.label || `${group.title} Option ${visibleOptionIndex + 1}`]} explanations={visibleOptionIndex === undefined ? group.explanations : [group.explanations?.[visibleOptionIndex] ?? ""]} status={status} preserveExactText allowApply={canApply} onApplySuggestion={(text, index) => apply(text, visibleOptionIndex ?? index)} appliedSuggestionIndex={visibleOptionIndex === undefined ? selectedIndex : selectedIndex === visibleOptionIndex ? 0 : null}
      onUndoAppliedSuggestion={change.changeSetId ? async () => { await undo(); } : undefined} onSendSuggestionToNewDocument={onSendSuggestionToNewDocument} />}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}
